import React, { useEffect, useMemo } from 'react';
import { Box, TextField, Button, IconButton, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Public as PublicIcon, Lock as LockIcon } from '@mui/icons-material';
import { en } from '../translations/en';
import { useSignatureData } from '../hooks/useSignatureData';
import { filterTitleAndSignatureLocations } from '../utils/locationFilters';
import { AutocompleteFieldMultiSelectWithFavorites } from './AutocompleteFieldMultiSelectWithFavorites';
import { useWizardAccordionVariant } from './edit-object/WizardAccordionStyleContext';
import { Field } from './edit-object/design/Field';
import { Input } from './edit-object/design/Form';

interface Signature {
  id?: string;  // Stable key for React
  type: string[];
  location: string[];
  display: string;
  isPublic?: boolean;  // Individual privacy control
}

interface SignatureFieldsProps {
  value?: Signature[];
  onChange?: (signatures: Signature[]) => void;
  /**
   * 'mui' (default) keeps the legacy MUI TextField + Autocomplete pickers
   * with notched-outline labels. 'design' opts the signature display
   * TextField and its child Autocompletes into the Field+Input primitives
   * so labels match the rest of the edit-object hi-fi panel.
   */
  variant?: 'mui' | 'design';
}

// Default empty signature - created once to avoid UUID generation on every render
const createDefaultSignature = (): Signature => ({
  id: crypto.randomUUID(),
  type: [],
  location: [],
  display: '',
  isPublic: true,
});

/** Migrate legacy string type to array format */
const migrateSignatureType = (type: string | string[] | undefined): string[] => {
  if (Array.isArray(type)) return type;
  if (typeof type === 'string' && type) return [type];
  return [];
};

/** Migrate legacy string location to array format */
const migrateSignatureLocation = (location: string | string[] | undefined): string[] => {
  if (Array.isArray(location)) return location;
  if (typeof location === 'string' && location) return [location];
  return [];
};

export const SignatureFields = ({ value, onChange, variant = 'mui' }: SignatureFieldsProps) => {
  const isDescribeVariant = useWizardAccordionVariant() === 'describe';
  // Memoize default to avoid creating new UUID on every render when value is undefined
  const defaultSignatures = useMemo(() => [createDefaultSignature()], []);
  const signatures = value || defaultSignatures;
  const { signatureTypes, signatureLocations, loading } = useSignatureData();
  const filteredLocations = useMemo(() => filterTitleAndSignatureLocations(signatureLocations), [signatureLocations]);

  // Ensure we always have at least one signature
  useEffect(() => {
    if (signatures.length === 0) {
      onChange?.([createDefaultSignature()]);
    }
  }, [signatures.length, onChange]);

  // Migrate legacy string type and location to array format
  useEffect(() => {
    if (!value) return;
    const needsMigration = value.some(
      sig => typeof sig.type === 'string' || typeof sig.location === 'string'
    );
    if (needsMigration) {
      const migrated = value.map(sig => ({
        ...sig,
        type: migrateSignatureType(sig.type),
        location: migrateSignatureLocation(sig.location as string | string[]),
      }));
      onChange?.(migrated);
    }
  }, [value, onChange]);

  const addSignature = () => {
    const newSignatures = [...signatures, createDefaultSignature()];
    onChange?.(newSignatures);
  };

  const removeSignature = (index: number) => {
    if (signatures.length > 1) {
      const newSignatures = signatures.filter((_, i) => i !== index);
      onChange?.(newSignatures);
    }
  };

  const updateSignature = (index: number, field: keyof Signature, value: string | string[] | boolean) => {
    const updated = signatures.map((sig, i) =>
      i === index ? { ...sig, [field]: value } : sig
    );
    onChange?.(updated);
  };

  return (
    <Box>
      {/* First signature - always visible with the three main fields */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={
            variant === 'design'
              ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }
              : { mb: 2 }
          }
        >
          <Box sx={variant === 'design' ? undefined : { mb: 2 }}>
            <AutocompleteFieldMultiSelectWithFavorites
              label={en.wizard.describe.steps.signature.signatureType.label}
              value={signatures[0]?.type || []}
              options={signatureTypes}
              onChange={(value) => updateSignature(0, 'type', value)}
              loading={loading}
              vocabularyType="signature_type"
              variant={variant}
            />
          </Box>
          <Box>
            <AutocompleteFieldMultiSelectWithFavorites
              label={en.wizard.describe.steps.signature.locationOfSignature.label}
              value={signatures[0]?.location || []}
              options={filteredLocations}
              onChange={(value) => updateSignature(0, 'location', value)}
              loading={loading}
              vocabularyType="signature_location"
              variant={variant}
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {en.wizard.describe.steps.signature.signatureDisplay.subtitle}
        </Typography>
        {variant === 'design' ? (
          <Field label={en.wizard.describe.steps.signature.signatureDisplay.label}>
            <Input
              aria-label={en.wizard.describe.steps.signature.signatureDisplay.label}
              value={signatures[0]?.display || ''}
              onChange={(e) => updateSignature(0, 'display', e.target.value)}
            />
          </Field>
        ) : (
          <TextField
            fullWidth
            label={en.wizard.describe.steps.signature.signatureDisplay.label}
            variant="outlined"
            value={signatures[0]?.display || ''}
            onChange={(e) => updateSignature(0, 'display', e.target.value)}
          />
        )}

        {!isDescribeVariant && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Tooltip title={signatures[0]?.isPublic !== false ? "Public - Click to make private" : "Private - Click to make public"}>
              <IconButton
                onClick={() => updateSignature(0, 'isPublic', signatures[0]?.isPublic === false ? true : false)}
                size="small"
                sx={{ color: signatures[0]?.isPublic !== false ? 'success.main' : 'text.secondary' }}
              >
                {signatures[0]?.isPublic !== false ? <PublicIcon /> : <LockIcon />}
              </IconButton>
            </Tooltip>
            <Box component="span" sx={{ ml: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
              {signatures[0]?.isPublic !== false ? 'Public' : 'Private'}
            </Box>
          </Box>
        )}
      </Box>

      {/* Additional signatures */}
      {signatures.slice(1).map((signature, index) => {
        const actualIndex = index + 1;
        return (
          <Card key={signature.id || actualIndex} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                {!isDescribeVariant ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={signature.isPublic !== false ? "Public - Click to make private" : "Private - Click to make public"}>
                      <IconButton
                        onClick={() => updateSignature(actualIndex, 'isPublic', signature.isPublic === false ? true : false)}
                        size="small"
                        sx={{ color: signature.isPublic !== false ? 'success.main' : 'text.secondary' }}
                      >
                        {signature.isPublic !== false ? <PublicIcon /> : <LockIcon />}
                      </IconButton>
                    </Tooltip>
                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                      {signature.isPublic !== false ? 'Public' : 'Private'}
                    </Box>
                  </Box>
                ) : <Box />}
                <IconButton
                  onClick={() => removeSignature(actualIndex)}
                  size="small"
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>

              <Box sx={{ mb: 2 }}>
                <AutocompleteFieldMultiSelectWithFavorites
                  label={en.wizard.describe.steps.signature.signatureType.label}
                  value={signature.type || []}
                  options={signatureTypes}
                  onChange={(value) => updateSignature(actualIndex, 'type', value)}
                  loading={loading}
                  vocabularyType="signature_type"
                  variant={variant}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <AutocompleteFieldMultiSelectWithFavorites
                  label={en.wizard.describe.steps.signature.locationOfSignature.label}
                  value={signature.location || []}
                  options={filteredLocations}
                  onChange={(value) => updateSignature(actualIndex, 'location', value)}
                  loading={loading}
                  vocabularyType="signature_location"
                  variant={variant}
                />
              </Box>

              {variant === 'design' ? (
                <Field label={en.wizard.describe.steps.signature.signatureDisplay.label}>
                  <Input
                    aria-label={en.wizard.describe.steps.signature.signatureDisplay.label}
                    value={signature.display}
                    onChange={(e) => updateSignature(actualIndex, 'display', e.target.value)}
                  />
                </Field>
              ) : (
                <TextField
                  fullWidth
                  label={en.wizard.describe.steps.signature.signatureDisplay.label}
                  variant="outlined"
                  value={signature.display}
                  onChange={(e) => updateSignature(actualIndex, 'display', e.target.value)}
                />
              )}
            </CardContent>
          </Card>
        );
      })}

      <Button
        startIcon={<Add />}
        onClick={addSignature}
        variant="outlined"
        sx={{ mt: 1 }}
      >
        {en.wizard.describe.steps.signature.addButton}
      </Button>
    </Box>
  );
};
